/**
 * RM commissions client (P26, Admin-only) — backend routers/commissions.py.
 * Increment 1: commission tables (marginal bands on an RM's ANNUAL credited fees, the same
 * shape as client fee tranches) and one table per RM.
 */
import { api } from './client';
import type { FeeTrancheIn } from './feeSchedules';
import type { UUID } from './types';

export interface CommissionTrancheOut {
  seq: number;
  min_eur_cents: number;
  max_eur_cents: number | null;
  rate_bps: number;
}

export interface CommissionTable {
  id: UUID;
  name: string;
  description: string | null;
  tranches: CommissionTrancheOut[];
  assigned_count: number;
  created_at: string;
  updated_at: string;
}

export interface CommissionTableIn {
  name: string;
  description: string | null;
  tranches: FeeTrancheIn[];
}

export interface CommissionAssignment {
  user_id: UUID;
  user_label: string;
  role: string;
  named_agreements: number;
  /** Agreements whose current fee split includes this person. */
  split_agreements: number;
  table_id: UUID | null;
  table_name: string | null;
}

/** The agreement's fee split (Pedro 2026-10-10: any users, effective-dated sets). */
export interface CommissionShareRow { user_id: UUID; user_label: string; share_bps: number }
export interface NamedRm { user_id: UUID; user_label: string; is_primary: boolean }
export interface AgreementCommissionShares {
  agreement_id: UUID;
  named: NamedRm[];
  history: { effective_from: string; shares: CommissionShareRow[] }[];   // newest first
  as_of: string;
  in_force: CommissionShareRow[];
  /** Why the compute would stop on this agreement (no split / not 100% / no table), or null. */
  problem: string | null;
}

/** One agreement on the Fee split tab. */
export interface FeeSplitRow {
  agreement_id: UUID;
  agreement_label: string;
  agreement_status: string;
  named: NamedRm[];
  in_force: CommissionShareRow[];
  problem: string | null;
}

/** Increment 3: the month's commissions, computed when read (Pedro 2026-10-10). */
export interface CommissionLine {
  agreement_id: UUID;
  agreement_label: string;
  billed_cents: number;
  annualized_cents: number;
  share_bps: number;
  credited_cents: number;
  annual_base_cents: number;
}
export interface CommissionPerson {
  user_id: UUID;
  user_label: string;
  table_name: string | null;
  credited_cents: number;
  annual_base_cents: number;
  effective_rate_pct: number | null;
  commission_cents: number;
  lines: CommissionLine[];
}
export interface CommissionMonth {
  period_month: string;
  people: CommissionPerson[];
  /** Fees billed with no fee split — the screen pauses on "Proceed or Correct?". */
  uncredited: {
    agreement_id: UUID; agreement_label: string; billed_cents: number; named_rms: string[];
    /** A split exists but starts after this month (smoke 2026-10-11, S12). */
    later_split_from?: string | null;
  }[];
  /** Blocking: must be corrected before any result is used. */
  problems: string[];
  total_billed_cents: number;
  total_credited_cents: number;
  total_commission_cents: number;
}

export const commissionsApi = {
  /** The split in force for `asOf`'s month (the page's month — the same one Monthly computes). */
  feeSplits: (asOf: string) => api.get<FeeSplitRow[]>(`/commissions/fee-splits?as_of=${asOf}`),
  monthly: (month: string) => api.get<CommissionMonth>(`/commissions/monthly?month=${month}`),
  /** Same-origin GET (session cookie); `proceed` confirms fees credited to nobody. */
  monthlyXlsxHref: (month: string, proceed: boolean) =>
    `/commissions/monthly.xlsx?month=${month}${proceed ? '&proceed=true' : ''}`,
  shares: (agreementId: UUID, asOf: string) =>
    api.get<AgreementCommissionShares>(`/commissions/agreements/${agreementId}/shares?as_of=${asOf}`),
  setShares: (agreementId: UUID, effectiveFrom: string, shares: { user_id: UUID; share_bps: number }[]) =>
    api.put<AgreementCommissionShares>(`/commissions/agreements/${agreementId}/shares`,
      { effective_from: effectiveFrom, shares }),
  tables: () => api.get<CommissionTable[]>('/commissions/tables'),
  createTable: (b: CommissionTableIn) => api.post<CommissionTable>('/commissions/tables', b),
  updateTable: (id: UUID, b: CommissionTableIn) => api.put<CommissionTable>(`/commissions/tables/${id}`, b),
  deleteTable: (id: UUID) => api.delete<void>(`/commissions/tables/${id}`),
  assignments: () => api.get<CommissionAssignment[]>('/commissions/assignments'),
  assign: (userId: UUID, tableId: UUID | null) =>
    api.put<CommissionAssignment[]>(`/commissions/assignments/${userId}`, { table_id: tableId }),
};
